(() => {
  'use strict';

  const product = window.__ARTO_SHARED_INVENTORY_PRODUCT__;
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  if (!variants.length) return;

  const API =
    window.ARTOMATOR_PATCH_API_BASE ||
    'https://us-central1-a2-migration.cloudfunctions.net/api';
  const normalizeSku = value => String(value || '').trim().toUpperCase();
  const variantById = new Map(
    variants.map(variant => [String(variant.id), variant])
  );
  const managed = new Map();
  let loading = true;
  let inventoryError = '';
  let syncing = false;

  // Cart holds are intentionally ignored. A blank is sellable until physical
  // stock has been committed to an order.
  const availableFor = item =>
    Math.max(0, Number(item?.onHand || 0) - Number(item?.committed || 0));

  const setProductBadgeState = () => {
    const originalBadges = [
      ...document.querySelectorAll('[data-arto-shared-inventory-badge]')
    ];
    const generatedWrappers = [
      ...document.querySelectorAll('[data-arto-shared-inventory-badge-wrapper]')
    ];

    // Products that Artomator does not manage keep Shopify's original badge.
    if (!managed.size) {
      originalBadges.forEach(badge => {
        badge.hidden = false;
        badge.classList.remove('hide');
        badge.style.removeProperty('display');
      });
      generatedWrappers.forEach(wrapper => wrapper.remove());
      return;
    }

    const soldOut = ![...managed.values()].some(item => availableFor(item) > 0);
    originalBadges.forEach(badge => {
      badge.hidden = !soldOut;
      badge.classList.toggle('hide', !soldOut);
      if (soldOut) badge.style.removeProperty('display');
      else badge.style.setProperty('display', 'none', 'important');
    });

    if (!soldOut || originalBadges.length) {
      generatedWrappers.forEach(wrapper => wrapper.remove());
      return;
    }

    document.querySelectorAll('.tt-product-single-info').forEach(container => {
      if (container.querySelector('[data-arto-shared-inventory-badge-wrapper]')) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'tt-wrapper';
      wrapper.dataset.artoSharedInventoryBadgeWrapper = 'true';
      const labels = document.createElement('div');
      labels.className = 'tt-label';
      const badge = document.createElement('div');
      badge.className = 'tt-label-out-stock';
      badge.dataset.artoSharedInventoryGenerated = 'true';
      badge.textContent = product.soldOutLabel || 'Sold Out';
      labels.appendChild(badge);
      wrapper.appendChild(labels);
      container.prepend(wrapper);
    });
  };

  const forms = () =>
    [...document.querySelectorAll('form[action*="/cart/add"]')];

  const selectedVariant = form =>
    variantById.get(String(form?.querySelector('[name="id"]')?.value || '')) ||
    null;

  const field = (form, key, name) => {
    let input = form.querySelector(`[data-arto-shared-inventory="${key}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.dataset.artoSharedInventory = key;
      form.appendChild(input);
    }
    return input;
  };

  const quantityFor = form =>
    Math.max(1, Number(form.querySelector('[name="quantity"]')?.value || 1));

  const setButtonState = (form, state, message) => {
    form.querySelectorAll('.btn-addtocart').forEach(button => {
      button.dataset.artoSharedInventoryState = state;
      button.disabled = state !== 'available';
      button.setAttribute('aria-disabled', state === 'available' ? 'false' : 'true');
      button.textContent = message;
      button.classList.toggle('disable', state !== 'available');
      button.style.setProperty(
        'background',
        state === 'available' ? '#64B562' : state === 'sold-out' ? '#BF1C1E' : '#9B9B9B',
        'important'
      );
      button.style.setProperty('pointer-events', state === 'available' ? 'auto' : 'none', 'important');
      button.style.setProperty('opacity', '1', 'important');
    });
  };

  const hasExplicitSize = () =>
    variants.length === 1 ||
    Boolean(document.querySelector('.new-options-list li.active'));

  const syncForm = form => {
    const variant = selectedVariant(form);
    const sku = normalizeSku(variant?.sku);
    const stock = managed.get(sku);
    if (!stock) {
      if (form.dataset.artoSharedInventoryManaged === 'true') {
        form.dataset.artoSharedInventoryManaged = 'false';
        const skuInput = form.querySelector('[data-arto-shared-inventory="sku"]');
        if (skuInput) skuInput.value = '';
      }
      return false;
    }

    form.dataset.artoSharedInventoryManaged = 'true';
    form.artoSharedInventoryAddToCart = addToCart => validateAndAdd(form, addToCart);
    field(form, 'sku', 'properties[_arto_inventory_sku]').value = sku;

    if (!hasExplicitSize()) {
      setButtonState(form, 'waiting', 'Select Size');
    } else if (loading) {
      setButtonState(form, 'loading', 'Checking Stock…');
    } else if (inventoryError) {
      setButtonState(form, 'error', 'Try Again');
    } else if (availableFor(stock) < quantityFor(form)) {
      setButtonState(form, 'sold-out', 'Sold Out');
    } else {
      setButtonState(form, 'available', 'Add to Cart');
    }
    return true;
  };

  const variantForOption = value => {
    const clean = String(value || '').trim().toLowerCase();
    return variants.find(variant =>
      [...(variant.options || []), variant.title]
        .filter(Boolean)
        .some(option => String(option).trim().toLowerCase() === clean)
    );
  };

  const syncOptions = () => {
    document.querySelectorAll('.new-options-list li').forEach(item => {
      const link = item.querySelector('a');
      const variant = variantForOption(link?.dataset?.value || link?.textContent);
      const stock = managed.get(normalizeSku(variant?.sku));
      if (!stock) return;
      const soldOut = availableFor(stock) < 1;
      item.dataset.artoSharedInventorySoldOut = soldOut ? 'true' : 'false';
      item.dataset.availability = soldOut ? 'false' : 'true';
      item.classList.toggle('disabled', soldOut);
      item.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
      link?.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
      if (link) link.title = soldOut ? 'Sold out' : '';
    });
  };

  const syncUi = () => {
    setProductBadgeState();
    syncOptions();
    forms().forEach(syncForm);
  };

  const refresh = async () => {
    if (syncing) return;
    syncing = true;
    if (!managed.size) loading = true;
    try {
      const skus = [...new Set(variants.map(variant => normalizeSku(variant.sku)).filter(Boolean))];
      const response = await fetch(
        `${API}/patch-programs/blanks/inventory?skus=${encodeURIComponent(skus.join(','))}`,
        { cache: 'no-store', headers: { Accept: 'application/json' } }
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || 'Inventory is unavailable.');
      managed.clear();
      (result.items || []).forEach(item => managed.set(normalizeSku(item.sku), item));
      inventoryError = '';
      if (managed.size) document.body.classList.add('arto-shared-blank-product');
    } catch (error) {
      inventoryError = error.message || 'Inventory is unavailable.';
    } finally {
      loading = false;
      syncing = false;
      syncUi();
    }
  };

  async function validateAndAdd(form, addToCart) {
    if (form.dataset.artoSharedInventoryAdding === 'true') return false;
    const variant = selectedVariant(form);
    const sku = normalizeSku(variant?.sku);
    const stock = managed.get(sku);
    const quantity = quantityFor(form);
    if (!stock || availableFor(stock) < quantity) {
      await refresh();
      return false;
    }

    form.dataset.artoSharedInventoryAdding = 'true';
    setButtonState(form, 'loading', 'Adding…');
    try {
      const selectionChanged =
        normalizeSku(selectedVariant(form)?.sku) !== sku || quantityFor(form) !== quantity;
      if (selectionChanged) return false;

      field(form, 'sku', 'properties[_arto_inventory_sku]').value = sku;
      await addToCart();
      return true;
    } catch (error) {
      inventoryError = error.message || 'This item could not be added.';
      return false;
    } finally {
      form.dataset.artoSharedInventoryAdding = 'false';
      syncUi();
    }
  }

  document.addEventListener('click', event => {
    const soldOut = event.target.closest?.('[data-arto-shared-inventory-sold-out="true"]');
    if (!soldOut) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    if (form?.dataset?.artoSharedInventoryManaged !== 'true') return;
    if (form.dataset.artoSharedInventoryCartReady === 'true') {
      form.dataset.artoSharedInventoryCartReady = 'false';
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    const submitter = event.submitter;
    validateAndAdd(form, () => {
      form.dataset.artoSharedInventoryCartReady = 'true';
      form.requestSubmit(submitter || undefined);
    });
  }, true);

  document.addEventListener('click', () => window.setTimeout(syncUi, 0), true);
  document.addEventListener('change', syncUi, true);
  window.setInterval(syncUi, 500);
  window.setInterval(refresh, 30000);
  refresh();
})();
